/**
 * W46 — Dashboards index (/dashboards/charts).
 * Chart-first, live, no exports.
 * Role tab nav: Home | Pipeline & Forecast | Account Health | Executive
 *
 * Visual canon: 5-color palette, teal accents, no exclamation points, no emojis.
 * M-PACT hyphenated. "Checkpoint" not "Gate".
 */

import { useState } from "react";
import { getSession } from "@/lib/session";
import RoleHome from "./role-home";
import PipelineForecast from "./pipeline-forecast";
import AccountHealth from "./account-health";
import ExecutiveDashboard from "./executive";
import MarketingDashboardPage from "./marketing"; // W48

type Tab = "role-home" | "pipeline-forecast" | "account-health" | "executive" | "marketing";

const ALL_TABS: { key: Tab; label: string }[] = [
  { key: "role-home",         label: "Home" },
  { key: "pipeline-forecast", label: "Pipeline and Forecast" },
  { key: "account-health",    label: "Account Health" },
  { key: "executive",         label: "Executive" },
  { key: "marketing",         label: "Marketing" }, // W48
];

export default function DashboardsIndex() {
  const session = getSession();
  const role = session?.role ?? "ae";

  const defaultTab: Tab =
    role === "executive" || role === "manager" ? "executive" : "role-home";

  const [activeTab, setActiveTab] = useState<Tab>(defaultTab);

  const visibleTabs = ALL_TABS.filter((t) => {
    if (t.key === "executive" && role !== "manager" && role !== "executive") return false;
    return true;
  });

  return (
    <div className="flex flex-col min-h-screen bg-[hsl(var(--background))]">
      <div className="border-b border-border bg-[hsl(var(--surface))] px-4 pt-3">
        <h1 className="text-[13px] font-semibold text-muted-foreground mb-2 uppercase tracking-wide">
          Dashboards
        </h1>
        <nav className="flex gap-0 -mb-px" aria-label="Dashboard sections">
          {visibleTabs.map((tab) => {
            const active = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={`px-4 py-2 text-[13px] font-medium border-b-2 transition-colors whitespace-nowrap focus:outline-none ${
                  active
                    ? "border-[#0F766E] text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground hover:border-border"
                }`}
                aria-selected={active}
              >
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>
      <div className="flex-1 overflow-auto p-4 md:p-6">
        {activeTab === "role-home"         && <RoleHome role={role} />}
        {activeTab === "pipeline-forecast" && <PipelineForecast />}
        {activeTab === "account-health"    && <AccountHealth />}
        {activeTab === "executive"         && <ExecutiveDashboard />}
        {activeTab === "marketing"         && <MarketingDashboardPage />}
      </div>
    </div>
  );
}
